import { PLANE_DEFAULT_Z } from '@Components/BigBrowse/BigBrowseConfig';
import type { CardTextProps } from '@Components/BigBrowse/GridCardMesh/Components/Texts/CardText';
import { CardText } from '@Components/BigBrowse/GridCardMesh/Components/Texts/CardText';
import { transparencyColors } from '@Components/BigBrowse/GridCardMesh/gridCardConstants';
import type { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import { useBbContentPresenter } from '@Components/BigBrowse/bb2/BbContentPresenter';
import { ProductCardRounded } from '@Components/BigBrowse/bb2/meshes2/primitives/ProductCardRounded';
import { useGetTextBlockSize } from '@Utils/3d';
import { Assets } from '@Utils/AssetsStore';
import { Box, Flex } from '@react-three/flex';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';

import { ArrowRight } from '../../../GridCardMesh/Components/ArrowRight/ArrowRight';
import type { GridCardMeshProps } from '../GridCardMesh2';
import { ProductCardIrregular } from '../primitives/ProductCardIrregular';

export const ShopHeroCard = observer(function ShopHeroCard({
  item,
  onClick,
}: GridCardMeshProps<PlaneTypes.Card_ShopFloor_Hero>) {
  const {
    enableCollapsedDisplaysFX,
    enableSuppressHeroBackgroundFX,
    isExploded,
  } = useBbContentPresenter();
  const { heroesLoaded } = useBbContentPresenter();

  const imageUrl = enableSuppressHeroBackgroundFX.value
    ? undefined
    : item.data?.imageUrl;

  const [realHeight, setRealHeight] = useState<number | undefined>(undefined);

  const titleProps: CardTextProps = {
    text: item.data?.title ?? '',
    fontAsset: Assets.Font_Gilroy_Bold,
    fontSize: 44,
    textAlign: 'center',
    color: 'white',
    position: { x: 0, y: 0 },
    anchorX: 'center',
    anchorY: 'middle',
    maxWidth: item.size.width - 60,
    lineHeight: 0.95,
  };

  const bodyPros: CardTextProps = {
    text: item.data?.body ?? '',
    color: 'white',
    fontSize: 14,
    fontAsset: Assets.Font_Gilroy_SemiBold,
    anchorX: 'center',
    anchorY: 'middle',
    textAlign: 'center',
    maxWidth: item.size.width - 60,
    letterSpacing: 0.03,
    lineHeight: 0.9,
  };

  // There is an issue here were sometimes, the 'useGetTextBlockSize' returns different text height
  // from what is actually rendered. This makes the 'Flex' boxes render with different height than
  // the text they wrap, so they alignment on screen does not work.
  // Usually this happens if the text is near the length of a single line.
  // To fix this, we render first a text for an instant with the 'title' height, so we can capture the
  // actually rendered height.

  const { height: titleHeight } =
    useGetTextBlockSize(titleProps, { applyScaleFix: false }) ?? {};

  if (titleHeight === undefined) {
    // Fix Step 1: If we still have not the titleHeight calculation we return null
    return null;
  }

  if (titleHeight !== undefined && !realHeight) {
    // Fix Step 2: If we have titleHeight only, then we render a temp text to measure its height
    return (
      <CardText
        {...titleProps}
        onHeightChange={setRealHeight}
        visible={false}
      />
    );
  }

  const maskOpacity =
    enableSuppressHeroBackgroundFX.value && !isExploded.value ? 1 : 0;

  // Fix Step 3: Since we measured the real height, we can now render the actual card
  return (
    <>
      <ProductCardRounded // This is for fade in-and-out the black background when zoom-in or out
        position={item.worldPosition}
        size={item.size.width}
        uniqueKey=''
        colorOpacity={maskOpacity}
      />
      <ProductCardIrregular
        position={item.worldPosition}
        size={item.size.width}
        imageUrl={imageUrl}
        transparencyColor={transparencyColors[item.type]}
        onClick={onClick}
        uniqueKey={item.key ?? ''}
        onImageLoad={() => heroesLoaded.add(item.key!)}
        useCache
      >
        <Flex
          position={[0, 0, PLANE_DEFAULT_Z + 0.2]}
          size={[item.size.width, item.size.height, 0]}
          alignItems='center'
          justifyContent='center'
          centerAnchor
        >
          <Box width={titleProps.maxWidth} height={realHeight} centerAnchor>
            <CardText {...titleProps} />
          </Box>
          {bodyPros.text && !enableCollapsedDisplaysFX.value && (
            <Box
              width={bodyPros.maxWidth}
              height={14}
              centerAnchor
              marginTop={10}
            >
              <CardText {...bodyPros} />
            </Box>
          )}
          {!enableCollapsedDisplaysFX.value && (
            <Box
              width={item.size.width}
              height={10}
              centerAnchor
              marginTop={10}
            >
              <ArrowRight lineWidth={1.5} position={[0, 0, 0]} />
            </Box>
          )}
        </Flex>
      </ProductCardIrregular>
    </>
  );
});
